// src/pages/admin/InvoicedSalesCard.tsx
//
// Read-only "invoiced sales" card fed by the real imported FileMaker
// ledger (/api/admin/invoices). Shown on the admin Dashboard and the
// Order queue so staff see actual business activity even while the
// Shop order-request pipeline is empty.
//
// Honesty contract (M3 spec + AGENTS.md rule 4): these are FileMaker
// SALES INVOICES, never Shop orders. The card is labeled as such and
// links to the Invoices page; nothing here creates or implies order
// requests, and nothing is written to the orders table.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Receipt } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { useAuth } from "@/components/auth/AuthContext";
import { authHeaders } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";

interface InvoicedSaleRow {
  invoice_number: string;
  invoice_date: string | null;
  customer_name_snapshot: string | null;
  payment_method: string | null;
  total_tvac: string | number | null;
  line_count: number;
}

export function InvoicedSalesCard({ limit = 8 }: { limit?: number }) {
  const auth = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<InvoicedSaleRow[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    setError("");
    fetch(`/api/admin/invoices?limit=${limit}`, { headers: authHeaders() })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = (await r.json()) as { rows: InvoicedSaleRow[] };
        setRows(body.rows);
      })
      .catch((e) => setError(String(e instanceof Error ? e.message : e)));
  }, [auth.isReady, auth.isAuthenticated, limit]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-accent-amber" strokeWidth={1.5} />
            {t("admin.invoicedSalesCard.title")}
          </CardTitle>
          <CardDescription>{t("admin.invoicedSalesCard.description")}</CardDescription>
        </div>
        <Button variant="ghost" size="sm" href="/admin/invoices">
          {t("admin.invoicedSalesCard.viewAll")}
          <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
        </Button>
      </CardHeader>
      <CardContent className="divide-y divide-border p-0">
        {error && (
          <p className="px-5 py-3 text-sm text-fg-tertiary">
            {t("admin.invoicedSalesCard.loadError", { error })}
          </p>
        )}
        {!error && rows === null && (
          <p className="px-5 py-3 text-sm text-fg-tertiary">
            {t("admin.invoicedSalesCard.loading")}
          </p>
        )}
        {!error && rows !== null && rows.length === 0 && (
          <p className="px-5 py-3 text-sm text-fg-tertiary">
            {t("admin.invoicedSalesCard.empty")}
          </p>
        )}
        {(rows ?? []).map((inv) => (
          <div
            key={inv.invoice_number}
            className="flex items-center justify-between gap-3 px-5 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-510 text-sm text-fg-primary">
                  {inv.customer_name_snapshot ?? t("admin.invoicedSalesCard.unknownCustomer")}
                </span>
                {inv.payment_method && (
                  <Badge variant="default" size="sm">
                    {inv.payment_method}
                  </Badge>
                )}
              </div>
              <div className="mt-0.5 text-xs text-fg-tertiary">
                {inv.invoice_number}
                {inv.invoice_date ? ` · ${inv.invoice_date}` : ""}
                {inv.line_count === 1
                  ? ` · ${t("admin.invoicedSalesCard.lineCount", {
                      count: inv.line_count,
                    })}`
                  : ` · ${t("admin.invoicedSalesCard.lineCountOther", {
                      count: inv.line_count,
                    })}`}
              </div>
            </div>
            <div className="shrink-0 text-sm font-510 text-fg-primary">
              {formatPrice(Number(inv.total_tvac ?? 0), "EUR")}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
